import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, type SsoSettingsView } from '../lib/api'

/**
 * OIDC single sign-on plus SCIM user provisioning. Two independent secrets live here: the OIDC
 * client secret (used server-side only, on every login) and the SCIM bearer token (used by the
 * identity provider to call back into this workspace) — both follow the same "shown once at
 * generation, never displayed again" contract as every other credential in Settings.
 */
export function SsoSettings() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [issuer, setIssuer] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [allowedDomain, setAllowedDomain] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [mintedToken, setMintedToken] = useState<string | null>(null)

  const settings = useQuery({ queryKey: ['sso-settings'], queryFn: api.ssoSettings, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['sso-settings'] })

  const save = useMutation({
    mutationFn: () =>
      api.saveSsoSettings({
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim() || undefined,
        allowedDomain: allowedDomain.trim(),
        enabled,
      }),
    onSuccess: () => {
      setEditing(false)
      setClientSecret('')
      void refresh()
    },
  })

  const disconnect = useMutation({ mutationFn: api.disconnectSso, onSuccess: refresh })
  const verifyDomain = useMutation({ mutationFn: api.verifySsoDomain, onSuccess: refresh })
  const rotateToken = useMutation({
    mutationFn: api.rotateScimToken,
    onSuccess: (result) => {
      setMintedToken(result.token)
      void refresh()
    },
  })
  const revokeToken = useMutation({
    mutationFn: api.revokeScimToken,
    onSuccess: () => {
      setMintedToken(null)
      void refresh()
    },
  })

  const needsAddon = settings.error instanceof ApiError && settings.error.status === 402

  if (settings.isPending) return <div className="cad-card" style={{ height: '240px' }} />

  if (needsAddon) {
    return (
      <>
        <header className="stack gap-1">
          <span className="cad-eyebrow">Platform</span>
          <h1 className="cad-title">SSO and SCIM</h1>
        </header>
        <section className="cad-card">
          <div className="stack gap-3">
            <p className="cad-muted" style={{ maxWidth: '64ch' }}>
              Let your team sign in through your identity provider (Okta, Entra ID, Google
              Workspace, or any OIDC-compliant provider) and have joiners and leavers
              provisioned automatically via SCIM.
            </p>
            <p className="cad-note">This needs the SSO and SCIM module. Add it from billing to turn it on.</p>
          </div>
        </section>
      </>
    )
  }

  if (settings.isError) {
    return (
      <div className="cad-card">
        <p className="cad-muted">Unavailable.</p>
      </div>
    )
  }

  const config = settings.data!
  const configured = Boolean(config.issuer)

  const startEditing = () => {
    setIssuer(config.issuer ?? '')
    setClientId(config.clientId ?? '')
    setClientSecret('')
    setAllowedDomain(config.allowedDomain ?? '')
    setEnabled(config.enabled)
    setEditing(true)
  }

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Platform</span>
        <h1 className="cad-title">SSO and SCIM</h1>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          OIDC login for your team, and SCIM provisioning for your identity provider to keep
          membership in sync automatically.
        </p>
      </header>

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">Single sign-on</h2>
            {configured ? (
              <span className="cad-pill" data-tone={config.enabled ? 'good' : undefined}>
                {config.enabled ? 'enabled' : 'configured, not enabled'}
              </span>
            ) : (
              <span className="cad-pill">not configured</span>
            )}
          </div>

          {configured && !editing && (
            <p className="cad-note">
              Issuer <b>{config.issuer}</b>, domain <b>@{config.allowedDomain}</b> (
              {config.domainVerifiedAt ? 'verified' : 'not yet verified'}). Point your identity
              provider's login button at:
              <br />
              <code style={{ wordBreak: 'break-all' }}>{config.loginUrl}</code>
            </p>
          )}

          {!editing && (
            <div className="row gap-2 wrap">
              <button type="button" className="cad-btn" data-variant="quiet" onClick={startEditing}>
                {configured ? 'Change' : 'Set up SSO'}
              </button>
              {configured && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={disconnect.isPending}
                  onClick={() => {
                    if (confirm(`Disconnect single sign-on?\n\nEveryone on this domain goes back to signing in with a password or an emailed code, and the identity provider stops governing who has access.`)) disconnect.mutate()
                  }}
                >
                  Disconnect
                </button>
              )}
            </div>
          )}

          {disconnect.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {disconnect.error instanceof ApiError || disconnect.error instanceof Error
                ? disconnect.error.message
                : 'Could not disconnect.'}
            </p>
          )}

          {editing && (
            <div className="stack gap-3" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px' }}>
              <div className="field-grid">
                <div className="cad-field">
                  <label htmlFor="sso-issuer">Issuer URL</label>
                  <input
                    id="sso-issuer"
                    className="cad-input"
                    value={issuer}
                    onChange={(e) => setIssuer(e.target.value)}
                    placeholder="https://your-org.okta.com"
                  />
                </div>
                <div className="cad-field">
                  <label htmlFor="sso-client-id">Client ID</label>
                  <input
                    id="sso-client-id"
                    className="cad-input"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </div>
                <div className="cad-field">
                  <label htmlFor="sso-client-secret">Client secret</label>
                  <input
                    id="sso-client-secret"
                    className="cad-input"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={config.hasClientSecret ? 'Leave blank to keep the current secret' : ''}
                  />
                </div>
                <div className="cad-field">
                  <label htmlFor="sso-domain">Allowed email domain</label>
                  <input
                    id="sso-domain"
                    className="cad-input"
                    value={allowedDomain}
                    onChange={(e) => setAllowedDomain(e.target.value)}
                    placeholder="example.com"
                  />
                </div>
              </div>
              <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                Only accounts whose email ends in this domain can complete an SSO login. Register{' '}
                <code style={{ wordBreak: 'break-all' }}>{config.loginUrl}</code> as this app's login
                URL with your identity provider, and{' '}
                <code style={{ wordBreak: 'break-all' }}>{config.redirectUri}</code> as its redirect
                URI.
              </p>

              <DomainVerificationPanel
                typedDomain={allowedDomain}
                config={config}
                verifyDomain={verifyDomain}
              />

              <label className="row gap-2" style={{ alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={!domainIsVerified(allowedDomain, config)}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enable SSO login for this workspace
              </label>
              {!domainIsVerified(allowedDomain, config) && (
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                  Verify domain ownership above before you can enable SSO.
                </p>
              )}

              {save.error && (
                <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                  {save.error instanceof ApiError || save.error instanceof Error
                    ? save.error.message
                    : 'That could not be saved.'}
                </p>
              )}

              <div className="row gap-2 wrap">
                <button
                  type="button"
                  className="cad-btn"
                  disabled={
                    !issuer.trim() ||
                    !clientId.trim() ||
                    !allowedDomain.trim() ||
                    // Blank is only valid once a secret already exists to fall back on — for a
                    // first-time setup the backend requires one, and leaving the button enabled
                    // here just bought an avoidable round trip to learn that.
                    (!config.hasClientSecret && !clientSecret.trim()) ||
                    save.isPending
                  }
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? 'Verifying…' : 'Save'}
                </button>
                <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">SCIM provisioning</h2>
            {config.scimTokenDisplayHint ? (
              <span className="cad-pill" data-tone="good">
                token active
              </span>
            ) : (
              <span className="cad-pill">no token</span>
            )}
          </div>
          <p className="cad-muted" style={{ maxWidth: '64ch' }}>
            Give your identity provider this base URL and bearer token to create, look up, and
            deactivate teammates automatically as they join or leave.
          </p>
          <p className="cad-note">
            <code style={{ wordBreak: 'break-all' }}>{config.scimUrl}</code>
          </p>

          {mintedToken ? (
            <div className="stack gap-2">
              <p className="cad-note" style={{ borderColor: 'var(--cad-warn, var(--cad-bad))' }}>
                Copy this token now — it will not be shown again.
              </p>
              <code
                className="cad-input"
                style={{ display: 'block', wordBreak: 'break-all', userSelect: 'all' }}
              >
                {mintedToken}
              </code>
              <div>
                <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setMintedToken(null)}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              {config.scimTokenDisplayHint && (
                <span className="cad-muted" style={{ fontFamily: 'monospace' }}>
                  {config.scimTokenDisplayHint}
                </span>
              )}
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={!configured || rotateToken.isPending}
                onClick={() => {
                  // Generating the first token breaks nothing; replacing a live one does.
                  if (!config.scimTokenDisplayHint || confirm(`Rotate the SCIM token?\n\nThe current token stops working the moment the new one is issued. Directory syncing fails until you paste the new token into your identity provider.`)) rotateToken.mutate()
                }}
              >
                {rotateToken.isPending ? 'Generating…' : config.scimTokenDisplayHint ? 'Rotate token' : 'Generate token'}
              </button>
              {config.scimTokenDisplayHint && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  disabled={revokeToken.isPending}
                  onClick={() => {
                    if (confirm(`Revoke the SCIM token?\n\nDirectory syncing stops immediately. New joiners and leavers will not be provisioned until a new token is issued.`)) revokeToken.mutate()
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          )}
          {!configured && <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>Set up SSO above before generating a token.</p>}
          {(rotateToken.error || revokeToken.error) && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {(() => {
                const err = rotateToken.error ?? revokeToken.error
                return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
              })()}
            </p>
          )}
        </div>
      </section>
    </>
  )
}

function normaliseDomain(value: string): string {
  return value.trim().toLowerCase().replace(/^@/, '')
}

/** True only when the domain currently typed in the form is the exact one the server has
 *  already verified — editing the field back to something unverified (even briefly, even to
 *  fix a typo) must re-disable "Enable" immediately, not wait for a save round trip. */
function domainIsVerified(typedDomain: string, config: SsoSettingsView): boolean {
  if (!config.domainVerifiedAt) return false
  return normaliseDomain(typedDomain) === (config.allowedDomain ?? '')
}

/**
 * The DNS proof-of-ownership step, shown inline in the editing form.
 *
 * Deliberately keyed off what the *server* has saved (`config.allowedDomain`), not what is
 * currently typed — the record to publish and the token to check are for the domain that was
 * actually submitted, and showing them for an unsaved edit would have an admin publish a DNS
 * record for a domain that never got saved, then wonder why verification keeps failing.
 */
function DomainVerificationPanel({
  typedDomain,
  config,
  verifyDomain,
}: {
  typedDomain: string
  config: SsoSettingsView
  verifyDomain: ReturnType<typeof useMutation<{ ok: true; verifiedAt: string }, Error, void>>
}) {
  const domainMatchesSaved = Boolean(config.allowedDomain) && normaliseDomain(typedDomain) === config.allowedDomain

  if (!domainMatchesSaved) {
    return normaliseDomain(typedDomain) ? (
      <p className="cad-note">Save your changes, then verify this domain below.</p>
    ) : null
  }

  if (config.domainVerifiedAt) {
    return (
      <p className="cad-note">
        <span className="cad-pill" data-tone="good">domain verified</span>{' '}
        {new Date(config.domainVerifiedAt).toLocaleDateString()}
      </p>
    )
  }

  return (
    <div className="stack gap-2">
      <p className="cad-note">
        Prove you control <b>{config.allowedDomain}</b> before SSO can be enabled. Add this DNS
        TXT record, then verify:
        <br />
        Name: <code style={{ wordBreak: 'break-all' }}>{config.domainVerificationRecord}</code>
        <br />
        Value: <code style={{ wordBreak: 'break-all', userSelect: 'all' }}>{config.domainVerificationToken}</code>
      </p>
      {verifyDomain.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {verifyDomain.error instanceof ApiError || verifyDomain.error instanceof Error
            ? verifyDomain.error.message
            : 'Could not verify the domain.'}
        </p>
      )}
      <div>
        <button
          type="button"
          className="cad-btn"
          data-variant="quiet"
          disabled={verifyDomain.isPending}
          onClick={() => verifyDomain.mutate()}
        >
          {verifyDomain.isPending ? 'Checking…' : 'Verify domain'}
        </button>
      </div>
    </div>
  )
}
